import { NeuSearchProps } from '@hcahealthcaredpx/neutron-react';

export interface TypeaheadSelectable {
  id: string;
  type: 'item';
  value: string;
  displayedValue?: string;
  data?: Record<string, unknown>;
}

export interface TypeaheadHeader {
  id: string;
  type: 'header';
  value: string;
}

export type TypeaheadItem = TypeaheadSelectable | TypeaheadHeader;

export type ExternalTypeaheadProps = {
  typeaheadResults: TypeaheadItem[];
  triggerTypeaheadLookup: (query: string) => Promise<void>;
};

export type InternalTypeaheadProps = {
  getTypeaheadItems: (query: string) => Promise<TypeaheadItem[]>;
};

/**
 * Props for the new Typeahead component.
 *
 * @property searchProps - Props for the search input, based on `NeuSearchProps` but with a required `name` property.
 * @property fieldProps - Props for the input field, based on `React.InputHTMLAttributes<HTMLInputElement>` but with a required `name` property.
 * @property typeaheadProps - Props for the typeahead functionality, combining either `ExternalTypeaheadProps` or `InternalTypeaheadProps` with additional options:
 *   @property initialSelectedItem - Optionally sets the initial selected item; runs once.
 *   @property updateSelectedItemRef - Ref to override the current selected item temporarily (not stateful).
 *   @property autoSelectOnBlur - If true, auto-selects the first result when the input loses focus.
 *   @property trackTypeaheadQuery - Callback to track the typeahead query externally.
 *   @property typeaheadLoading - Indicates if the typeahead is in a loading state.
 *   @property onTypeaheadKeyPress - Callback to react to key press events in the typeahead input.
 *   @property processTypeaheadSelection - Callback to process the selected item, returns a promise of the processed item.
 *   @property onTypeaheadSelect - Callback to react to item selection.
 *   @property resultsCTA - Optional call-to-action displayed at the bottom of results, with a label and link.
 */
export type NewTypeaheadProps = {
  searchProps: Omit<NeuSearchProps, 'name'> & {
    name: string;
  };
  fieldProps: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'> & {
    // force the name
    name: string;
  };
  typeaheadProps: (ExternalTypeaheadProps | InternalTypeaheadProps) & {
    // set an initial selected item on runs once
    initialSelectedItem?: TypeaheadItem | null;
    // override the current selected item temporarily (not stateful)
    updateSelectedItemRef?: React.Ref<(item: TypeaheadItem | null) => Promise<void>>;
    // auto select first result on blur
    autoSelectOnBlur?: boolean;
    // track the typeahead query externally
    trackTypeaheadQuery?: (query: string) => void;
    // loading state
    typeaheadLoading: boolean;
    // react to key press
    onTypeaheadKeyPress?: (value: string) => void;
    // process selection
    processTypeaheadSelection?: (item: TypeaheadItem) => Promise<TypeaheadItem>;
    // react to selection
    onTypeaheadSelect?: (item: TypeaheadItem) => void;
    // validation rules for typeaheadQuery (not field)
    typeaheadValidationRules?: ValidationRule[];
    // optional CTA at bottom of results
    resultsCTA?: {
      label: string;
      link: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
    };
  };
};

export type ValidationRule = (
  | {
      regex: RegExp;
    }
  | { customValidator: (value: string) => boolean }
) & {
  message: string;
};

export const isRegexpValidationRule = (
  rule: ValidationRule
): rule is { regex: RegExp; message: string } => {
  return (rule as { regex: RegExp }).regex !== undefined;
};

export const isExternalTypeaheadProps = (
  props: ExternalTypeaheadProps | InternalTypeaheadProps
): props is ExternalTypeaheadProps => {
  return (props as ExternalTypeaheadProps).typeaheadResults !== undefined;
};
